// SC-11348: regras do campo extra de upload (tipos aceitos, vários arquivos e
// limites). A API devolve `upload_settings` já resolvido; quando ele não vem
// (resposta antiga, preview, campo recém-criado), os mesmos padrões são
// aplicados aqui a partir do `field_configuration`.

export type UploadFileType = 'image' | 'pdf' | 'spreadsheet' | 'document'

export const UPLOAD_MAX_FILE_SIZE = 10 * 1024 * 1024
export const UPLOAD_MAX_FILES_LIMIT = 10
export const UPLOAD_MIN_MULTIPLE_FILES = 2
export const UPLOAD_DEFAULT_MAX_FILES = 5

interface UploadFileTypeDefinition {
  label: string
  extensions: string[]
  accept: Record<string, string[]>
}

export const UPLOAD_FILE_TYPES: Record<
  UploadFileType,
  UploadFileTypeDefinition
> = {
  image: {
    label: 'Imagem (JPG, PNG)',
    extensions: ['jpg', 'jpeg', 'png'],
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png']
    }
  },
  pdf: {
    label: 'PDF',
    extensions: ['pdf'],
    accept: { 'application/pdf': ['.pdf'] }
  },
  spreadsheet: {
    label: 'Planilha (CSV, XLSX)',
    extensions: ['csv', 'xlsx'],
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
        '.xlsx'
      ]
    }
  },
  document: {
    label: 'Documento de texto (DOC, DOCX)',
    extensions: ['doc', 'docx'],
    accept: {
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        ['.docx']
    }
  }
}

export const UPLOAD_FILE_TYPE_KEYS = Object.keys(
  UPLOAD_FILE_TYPES
) as UploadFileType[]

// Campo novo começa só com imagem e PDF; campo antigo (sem a chave) aceita todos.
export const UPLOAD_NEW_FIELD_DEFAULT_TYPES: UploadFileType[] = ['image', 'pdf']

export interface UploadSettings {
  accepted_types: UploadFileType[]
  accepted_extensions: string[]
  multiple: boolean
  max_files: number
  max_file_size: number
}

// Mesma forma do `field_configuration` gravado para o upload
export interface UploadFieldConfiguration {
  accepted_types?: UploadFileType[]
  multiple?: boolean
  max_files?: number
}

const isUploadFileType = (value: unknown): value is UploadFileType =>
  typeof value === 'string' &&
  (UPLOAD_FILE_TYPE_KEYS as string[]).includes(value)

const parseBoolean = (value: unknown) => value === true || value === 'true'

const clampMaxFiles = (value: unknown) => {
  const parsed = parseInt(String(value ?? ''), 10)
  if (!parsed) return UPLOAD_DEFAULT_MAX_FILES

  return Math.min(
    Math.max(parsed, UPLOAD_MIN_MULTIPLE_FILES),
    UPLOAD_MAX_FILES_LIMIT
  )
}

// Tipos válidos, na ordem fixa dos grupos. Vazio ou ausente vira "todos".
export const normalizeAcceptedTypes = (value: unknown): UploadFileType[] => {
  const types = Array.isArray(value) ? value.filter(isUploadFileType) : []

  if (!types.length) return [...UPLOAD_FILE_TYPE_KEYS]

  return UPLOAD_FILE_TYPE_KEYS.filter((type) => types.includes(type))
}

const extensionsFor = (types: UploadFileType[]) =>
  types.flatMap((type) => UPLOAD_FILE_TYPES[type].extensions)

const settingsFromConfiguration = (configuration: any): UploadSettings => {
  const config =
    configuration && typeof configuration === 'object' ? configuration : {}
  const acceptedTypes = normalizeAcceptedTypes(config.accepted_types)
  const multiple = parseBoolean(config.multiple)

  return {
    accepted_types: acceptedTypes,
    accepted_extensions: extensionsFor(acceptedTypes),
    multiple,
    max_files: multiple ? clampMaxFiles(config.max_files) : 1,
    max_file_size: UPLOAD_MAX_FILE_SIZE
  }
}

const settingsFromApi = (settings: any): UploadSettings => {
  const acceptedTypes = normalizeAcceptedTypes(settings.accepted_types)
  const multiple = parseBoolean(settings.multiple)

  return {
    accepted_types: acceptedTypes,
    accepted_extensions:
      Array.isArray(settings.accepted_extensions) &&
      settings.accepted_extensions.length
        ? settings.accepted_extensions.map(String)
        : extensionsFor(acceptedTypes),
    multiple,
    max_files: multiple ? clampMaxFiles(settings.max_files) : 1,
    max_file_size: Number(settings.max_file_size) || UPLOAD_MAX_FILE_SIZE
  }
}

// Aceita o campo em qualquer formato que circula no front: item do encoder do
// customer, CustomFieldItem (com `custom_field`), ExtraFieldItem ou o próprio
// registro do CustomField.
export const resolveUploadSettings = (field: any): UploadSettings => {
  const apiSettings =
    field?.upload_settings || field?.custom_field?.upload_settings

  if (apiSettings && typeof apiSettings === 'object') {
    return settingsFromApi(apiSettings)
  }

  return settingsFromConfiguration(
    field?.field_configuration || field?.custom_field?.field_configuration
  )
}

// Objeto `accept` do react-dropzone para os tipos aceitos
export const buildUploadAccept = (types: UploadFileType[]) =>
  types.reduce<Record<string, string[]>>((acc, type) => {
    Object.entries(UPLOAD_FILE_TYPES[type].accept).forEach(([mime, exts]) => {
      acc[mime] = [...(acc[mime] || []), ...exts]
    })
    return acc
  }, {})

const joinWithOr = (items: string[]) => {
  if (items.length <= 1) return items.join('')

  return `${items.slice(0, -1).join(', ')} ou ${items[items.length - 1]}`
}

// "JPG, PNG ou PDF"
export const describeUploadTypes = (types: UploadFileType[]) =>
  joinWithOr(
    types.flatMap((type) =>
      UPLOAD_FILE_TYPES[type].extensions
        .filter((ext) => ext !== 'jpeg')
        .map((ext) => ext.toUpperCase())
    )
  )

// Rótulos curtos para listagens ("Imagem (JPG, PNG), PDF")
export const describeUploadTypeLabels = (types: UploadFileType[]) =>
  types.map((type) => UPLOAD_FILE_TYPES[type].label).join(', ')

export const formatUploadMaxSize = (bytes: number) =>
  `${Math.round(bytes / (1024 * 1024))} MB`

// "JPG, PNG ou PDF, até 10 MB cada. Máximo de 5 arquivos."
export const uploadHelperText = (settings: UploadSettings) => {
  const size = formatUploadMaxSize(settings.max_file_size)

  if (settings.multiple) {
    return `${describeUploadTypes(
      settings.accepted_types
    )}, até ${size} cada. Máximo de ${settings.max_files} arquivos.`
  }

  return `${describeUploadTypes(settings.accepted_types)}, até ${size}.`
}

export const uploadInvalidTypeMessage = (settings: UploadSettings) =>
  `Tipo de arquivo não aceito. Envie ${describeUploadTypes(
    settings.accepted_types
  )}.`

export interface UploadDocument {
  uuid: string
  doc_file_name?: string
  doc_file_url?: string
  doc_file_size?: number
}

// Hash Ruby serializado ("{"uuid"=>"...", ...}"): pega só o que o front usa
const parseRubyHashDocument = (value: string): UploadDocument | null => {
  const read = (key: string) =>
    value.match(new RegExp(`"${key}"\\s*=>\\s*"([^"]*)"`))?.[1]

  const uuid = read('uuid')
  if (!uuid) return null

  const size = value.match(/"doc_file_size"\s*=>\s*(\d+)/)?.[1]

  return {
    uuid,
    doc_file_name: read('doc_file_name'),
    doc_file_url: read('doc_file_url'),
    doc_file_size: size ? Number(size) : undefined
  }
}

const toUploadDocument = (item: any): UploadDocument | null => {
  if (!item) return null

  if (typeof item === 'object') {
    return item.uuid ? (item as UploadDocument) : null
  }

  if (typeof item !== 'string') return null

  const text = item.trim()
  if (!text) return null
  if (text.includes('=>')) return parseRubyHashDocument(text)

  return { uuid: text }
}

// Lê o valor de um campo upload em qualquer formato: objeto único, array de
// objetos, uuid, array de uuids, JSON de array ou hash Ruby.
export const extractUploadDocuments = (value: unknown): UploadDocument[] => {
  if (value === null || value === undefined || value === '') return []

  let parsed: unknown = value

  if (typeof value === 'string' && value.trim().startsWith('[')) {
    try {
      parsed = JSON.parse(value)
    } catch {
      parsed = value
    }
  }

  const items = Array.isArray(parsed) ? parsed : [parsed]

  return items
    .map(toUploadDocument)
    .filter((doc): doc is UploadDocument => !!doc)
}

export const uploadValueToUuids = (value: unknown) =>
  extractUploadDocuments(value).map((doc) => doc.uuid)

export const hasUploadValue = (value: unknown) =>
  uploadValueToUuids(value).length > 0

// Valor que vai para a API: array de uuids no campo com vários arquivos,
// uuid único (ou null) no campo de 1 arquivo.
export const toUploadFormValue = (uuids: string[], multiple: boolean) =>
  multiple ? uuids : uuids[0] ?? null

// Nomes dos arquivos separados por vírgula (para exibição em texto)
export const formatUploadFileNames = (value: unknown) =>
  extractUploadDocuments(value)
    .map((doc) => doc.doc_file_name || 'Arquivo enviado')
    .join(', ')

// Normaliza o valor de upload para o payload do CustomFieldValue, mantendo o
// formato que veio: array (vários arquivos) vira array de uuids, valor único
// vira o uuid. Sem arquivo, null.
export const normalizeUploadSubmitValue = (value: unknown) => {
  const uuids = uploadValueToUuids(value)
  if (!uuids.length) return null

  return Array.isArray(value) ? uuids : uuids[0]
}
